/**
 * Wizard shell for the offline conversion upload flow.
 *
 * Step order:
 *   preflight  → pick-action → input → consent → upload → results
 *
 * The preflight step (ECL prerequisites) runs first because failing it can
 * only be fixed in the Google Ads UI — surfacing it up front saves the user
 * from filling out a whole CSV only to bounce.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import type {
  ConversionActionSummary,
  ECLPreflightResult,
  OfflineConversionEvent,
  Consent,
} from '@/services/api/offlineConversions';
import { ECLPrerequisitesStep } from './steps/ECLPrerequisitesStep';
import { ChooseConversionActionStep } from './steps/ChooseConversionActionStep';
import { EventsInputStep } from './steps/EventsInputStep';
import { ConsentStep } from './steps/ConsentStep';
import { UploadStep } from './steps/UploadStep';
import { ResultsStep } from './steps/ResultsStep';
import { initialWizardState, type InputMethod, type Step, type WizardState } from './types';

interface Props {
  googleAdsAccountId: string;
  onClose: () => void;
}

const STEP_TITLES: Record<Step, string> = {
  preflight: 'Account check',
  'pick-action': 'Conversion action',
  input: 'Events to upload',
  consent: 'Consent',
  upload: 'Send to Google',
  results: 'Results',
};

export const OfflineConversionUploadModal: React.FC<Props> = ({
  googleAdsAccountId,
  onClose,
}) => {
  const [state, setState] = useState<WizardState>(initialWizardState);

  const update = (patch: Partial<WizardState>) => setState((s) => ({ ...s, ...patch }));

  const handlePreflightPassed = (result: ECLPreflightResult) => {
    update({ preflight: result, step: 'pick-action' });
  };

  const handleActionPicked = (action: ConversionActionSummary) => {
    update({ conversionAction: action, step: 'input' });
  };

  const handleEventsChange = (events: OfflineConversionEvent[]) =>
    update({ events });
  const handleRejectionsChange = (
    parseRejections: Array<{ rowIndex: number; field?: string; reason: string }>
  ) => update({ parseRejections });
  const handleInputMethodChange = (m: InputMethod) => update({ inputMethod: m });

  const handleConsentChange = (consent: Consent) => update({ consent });

  const handleSubmitted = (requestId: string) =>
    update({ requestId, step: 'results' });

  const canAdvance = (): boolean => {
    switch (state.step) {
      case 'preflight':
        return state.preflight !== null;
      case 'pick-action':
        return state.conversionAction !== null;
      case 'input':
        return state.events.length > 0;
      case 'consent':
        return true;
      case 'upload':
        return false; // advance only via successful submit
      case 'results':
        return false;
    }
  };

  const nextStep = (): Step | null => {
    const order: Step[] = ['preflight', 'pick-action', 'input', 'consent', 'upload', 'results'];
    const idx = order.indexOf(state.step);
    return idx >= 0 && idx < order.length - 1 ? order[idx + 1] : null;
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="offline-conversion-upload-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <Card className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <div>
            <h2 id="offline-conversion-upload-title" className="text-lg font-semibold">
              Upload offline conversions
            </h2>
            <p className="text-xs text-gray-500">{STEP_TITLES[state.step]}</p>
          </div>
          <Button variant="ghost" onClick={onClose} aria-label="Close">
            ✕
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {state.step === 'preflight' && (
            <ECLPrerequisitesStep
              googleAdsAccountId={googleAdsAccountId}
              onPassed={handlePreflightPassed}
            />
          )}
          {state.step === 'pick-action' && (
            <ChooseConversionActionStep
              googleAdsAccountId={googleAdsAccountId}
              selectedId={state.conversionAction?.id ?? null}
              onSelect={handleActionPicked}
            />
          )}
          {state.step === 'input' && state.conversionAction && (
            <EventsInputStep
              conversionActionId={state.conversionAction.id}
              inputMethod={state.inputMethod}
              events={state.events}
              parseRejections={state.parseRejections}
              onInputMethodChange={handleInputMethodChange}
              onEventsChange={handleEventsChange}
              onRejectionsChange={handleRejectionsChange}
            />
          )}
          {state.step === 'consent' && (
            <ConsentStep consent={state.consent} onChange={handleConsentChange} />
          )}
          {state.step === 'upload' && state.conversionAction && (
            <UploadStep
              googleAdsAccountId={googleAdsAccountId}
              productDestinationId={state.conversionAction.id}
              events={state.events}
              consent={state.consent}
              onSubmitted={handleSubmitted}
            />
          )}
          {state.step === 'results' && state.requestId && (
            <ResultsStep
              googleAdsAccountId={googleAdsAccountId}
              requestId={state.requestId}
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-gray-200 p-4">
          <div className="text-xs text-gray-500">
            {state.events.length > 0 && state.step !== 'results' && (
              <span>{state.events.length} row{state.events.length === 1 ? '' : 's'} prepared</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              {state.step === 'results' ? 'Close' : 'Cancel'}
            </Button>
            {state.step !== 'upload' && state.step !== 'results' && (
              <Button
                variant="primary"
                onClick={() => {
                  const next = nextStep();
                  if (next) update({ step: next });
                }}
                disabled={!canAdvance()}
              >
                Continue
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
